import { channelToScheme } from '@affine/core/utils';
import { DebugLogger } from '@affine/debug';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';

const logger = new DebugLogger('open-in-app');

export const STREAMLAIN_DESKTOP_DOWNLOAD_MANIFEST_URL =
  'https://streamlain.de/desktop/stable/latest.json';
export const STREAMLAIN_DESKTOP_DOWNLOAD_FALLBACK_URL = 'https://streamlain.de';

type DesktopPlatform = 'linux' | 'macos' | 'windows';

type DesktopArtifact = {
  platform: DesktopPlatform | string;
  arch?: string;
  kind?: string;
  url?: string;
};

type DesktopReleaseManifest = {
  artifacts?: DesktopArtifact[];
};

type DesktopDownloadNavigator = Partial<
  Pick<Navigator, 'platform' | 'userAgent'>
> & {
  userAgentData?: {
    platform?: string;
    architecture?: string;
  };
};

type DesktopDownloadFetch = (
  input: string,
  init?: RequestInit
) => Promise<Pick<Response, 'json' | 'ok'>>;

const preferredArtifactKinds: Record<DesktopPlatform, string[]> = {
  linux: ['appimage', 'deb'],
  macos: ['dmg', 'zip'],
  windows: ['nsis', 'exe'],
};

export function detectDesktopPlatform(
  navigatorLike: DesktopDownloadNavigator = navigator
): DesktopPlatform {
  const platform = [
    navigatorLike.userAgentData?.platform,
    navigatorLike.platform,
    navigatorLike.userAgent,
  ]
    .filter(Boolean)
    .join(' ');

  if (/mac|darwin/i.test(platform)) {
    return 'macos';
  }
  if (/win/i.test(platform)) {
    return 'windows';
  }
  return 'linux';
}

function getPreferredArch(navigatorLike: DesktopDownloadNavigator) {
  const arch = navigatorLike.userAgentData?.architecture;
  if (/arm|aarch64/i.test(arch ?? '')) {
    return 'arm64';
  }
  return 'x64';
}

export function selectDesktopDownloadArtifact(
  artifacts: DesktopArtifact[],
  platform: DesktopPlatform,
  navigatorLike: DesktopDownloadNavigator = navigator
) {
  const preferredKinds = preferredArtifactKinds[platform];
  const preferredArch = getPreferredArch(navigatorLike);

  return artifacts
    .filter(artifact => artifact.platform === platform && artifact.url)
    .sort((left, right) => {
      const leftKindScore = preferredKinds.indexOf(left.kind ?? '');
      const rightKindScore = preferredKinds.indexOf(right.kind ?? '');
      const leftArchScore = left.arch === preferredArch ? 0 : 1;
      const rightArchScore = right.arch === preferredArch ? 0 : 1;

      return (
        (leftKindScore === -1 ? preferredKinds.length : leftKindScore) -
          (rightKindScore === -1 ? preferredKinds.length : rightKindScore) ||
        leftArchScore - rightArchScore
      );
    })[0];
}

export async function resolveDesktopDownloadUrl({
  fetcher = fetch,
  navigatorLike = navigator,
}: {
  fetcher?: DesktopDownloadFetch;
  navigatorLike?: DesktopDownloadNavigator;
} = {}) {
  try {
    const response = await fetcher(STREAMLAIN_DESKTOP_DOWNLOAD_MANIFEST_URL, {
      cache: 'no-store',
    });
    if (!response.ok) {
      return STREAMLAIN_DESKTOP_DOWNLOAD_FALLBACK_URL;
    }

    const manifest = (await response.json()) as DesktopReleaseManifest;
    const platform = detectDesktopPlatform(navigatorLike);
    const artifact = selectDesktopDownloadArtifact(
      manifest.artifacts ?? [],
      platform,
      navigatorLike
    );

    return artifact?.url ?? STREAMLAIN_DESKTOP_DOWNLOAD_FALLBACK_URL;
  } catch (e) {
    logger.error(
      `Failed to resolve ${BRAND_DISPLAY_NAME} desktop download URL`,
      e
    );
    return STREAMLAIN_DESKTOP_DOWNLOAD_FALLBACK_URL;
  }
}

export async function openDesktopDownload() {
  location.assign(await resolveDesktopDownloadUrl());
}

// return a StreamLain app URL to be opened in desktop app
export const getOpenUrlInDesktopAppLink = (
  url: string,
  newTab = true,
  scheme = channelToScheme[BUILD_CONFIG.appBuildType]
) => {
  try {
    if (!scheme) {
      return null;
    }

    const urlObject = new URL(url, location.origin);
    const params = urlObject.searchParams;

    if (newTab) {
      params.set('new-tab', '1');
    }
    if (environment.isSelfHosted) {
      // assume self-hosted server is the current origin
      params.set('server', location.origin);
    }
    return new URL(
      `${scheme}://${urlObject.host}${urlObject.pathname}?${params.toString()}#${urlObject.hash}`
    ).toString();
  } catch (e) {
    logger.error('Failed to get open url in desktop app link', e);
    return null;
  }
};
